/* AdminLogin — branded Mera Naksha login page (Navy + Gold).
 *
 * On submit hits POST /api/admin/auth/login which sets an httpOnly cookie,
 * then redirects to ?next= (default /admin). Handles 401 (bad creds),
 * 429 (rate-limit lockout), and generic network errors with friendly toasts.
 * Includes a `noindex,nofollow` meta tag so the URL never gets crawled.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/auth/AuthContext";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminLogin = () => {
  const { adminStatus: status, loginAdmin: login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Block search-engine indexing of the login URL.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    const prevTitle = document.title;
    document.title = "Admin Login · Mera Naksha";
    return () => {
      document.head.removeChild(tag);
      document.title = prevTitle;
    };
  }, []);

  // If we land here already authed (e.g. cookie still valid), bounce home.
  useEffect(() => {
    if (status === "authenticated") {
      navigate(next, { replace: true });
    }
  }, [status, next, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter both email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(next, { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) {
        setError(formatApiError(err.response.data?.detail));
      } else if (code === 401) {
        setError("Invalid email or password.");
      } else {
        setError(formatApiError(err.response?.data?.detail) || "Unable to sign in. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-navy text-white grid place-items-center px-4 py-10 relative overflow-hidden"
      data-testid="admin-login-page"
    >
      {/* Decorative gradient blobs */}
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center gap-3 mb-7">
          <div className="bg-white rounded-xl px-3 py-2 shadow-md">
            <BrandLogo size="md" asLink={false} withTagline lang="en" testId="login-brand-logo" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-bold tracking-widest uppercase">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure Admin Portal
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white text-foreground rounded-3xl shadow-2xl border-2 border-gold/30 overflow-hidden"
          data-testid="admin-login-form"
        >
          <div className="p-6 sm:p-8 space-y-5">
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-navy">
                Sign in to your dashboard
              </h1>
              <p className="text-[13px] text-muted-foreground mt-1">
                Team Mera Naksha members only. All access is logged.
              </p>
            </div>

            {error && (
              <div
                className="flex items-start gap-2.5 rounded-xl bg-red-50 border-2 border-red-200 px-3.5 py-2.5 text-[13px] text-red-800 font-semibold"
                data-testid="admin-login-error"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
                Email
              </span>
              <div className="mt-1.5 relative">
                <Mail className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@meranaksha.com"
                  data-testid="admin-login-email"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
                Password
              </span>
              <div className="mt-1.5 relative">
                <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                  className="w-full h-11 pl-9 pr-10 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="admin-login-toggle-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-sand-alt hover:text-navy"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full min-h-[48px] rounded-2xl bg-gold text-navy font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white active:scale-[0.99] transition-all shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" /> Sign in securely
                </>
              )}
            </button>

            <p className="text-[11.5px] text-muted-foreground text-center leading-snug">
              Forgot your password? Contact{" "}
              <a
                href="mailto:support@meranaksha.com"
                className="text-navy font-bold hover:text-gold-dark"
              >
                support@meranaksha.com
              </a>{" "}
              to reset it.
            </p>
          </div>

          <div className="px-6 sm:px-8 py-3 bg-navy/[0.04] border-t border-border text-[11px] text-muted-foreground flex items-center justify-between">
            <span>© Team Mera Naksha</span>
            <span className="inline-flex items-center gap-1 font-semibold">
              <Lock className="h-3 w-3" /> 256-bit encryption
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
